import { CrudFilter, LogicalFilter } from "@refinedev/core";

/**
 * A Typesense filter value. Plain tokens pass through; a string with a space, `|`, `,`, a bracket
 * or a non-ASCII letter (a `cityKey` such as `São Paulo|BR`) is backtick-quoted, which is how
 * Typesense reads a literal value. A value that is already quoted is left alone.
 */
const SAFE_VALUE = /^[A-Za-z0-9._-]*$/;
export const quoteFilterValue = (value: unknown): string => {
  if (typeof value !== "string") return String(value);
  if (SAFE_VALUE.test(value)) return value;
  if (value.length > 1 && value.startsWith("`") && value.endsWith("`"))
    return value;
  return `\`${value.replaceAll("`", "")}\``;
};

const isBlank = (value: unknown): boolean =>
  value === undefined || value === null || value === "";

export const transformFilter = (filter: CrudFilter): string | undefined => {
  const operatorMapping: { [key: string]: { pre: string; post?: string } } = {
    eq: {
      pre: ":=",
    },
    in: { pre: ":[", post: "]" },
    gte: {
      pre: ":>=",
    },
    gt: {
      pre: ":>",
    },
    lt: {
      pre: ":<",
    },
    lte: {
      pre: ":<=",
    },
    ina: {
      pre: ":[",
      post: "])",
    },
    contains: {
      pre: ":",
    },
  };

  if (filter.operator === "or") {
    const parts = (filter.value as CrudFilter[])
      .map(transformFilter)
      .filter((f) => f);
    return parts.length > 0 ? `(${parts.join(" || ")})` : undefined;
  }

  if (filter.operator in operatorMapping) {
    const { pre, post } = operatorMapping[filter.operator];
    // A cleared filter control sends `undefined`: it is no filter, not `field:=undefined`.
    if (isBlank(filter.value)) return undefined;
    const value =
      filter.operator === "in" && Array.isArray(filter.value)
        ? filter.value.map(quoteFilterValue).join(",")
        : filter.operator === "eq"
          ? quoteFilterValue(filter.value)
          : filter.value;
    return `${(filter as LogicalFilter).field}${pre}${value}${post ?? ""}`;
  }
};
